import cloneDeep from "lodash/cloneDeep";
import { defineStore } from "pinia";
import { StoreModule } from "@/constants/store";
import { i18n } from "@/plugins/i18n";
import type { ISelection } from "@/_store/selector.module";
import type { Component } from "vue";
import type { GenericCallback } from "@/types";

export interface DialogFooterBtn {
  title: string;
  spacer?: string;
  type?: string;
  cancel?: boolean;
  color?: string;
  callback?: GenericCallback;
  rounded?: boolean;
  variant?: "flat" | "text";
}

export interface DialogDataConfig<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous dialog item; mirrors ConfirmationDialogConfig<T> in @/types
  T = any,
  Action = string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- metadata shape varies per dialog type; constraining cascades through every dialog constructor
  Metadata extends object = { [key: string]: any },
> {
  action?: Action;
  item: T;
  selection?: ISelection<T> | string[];
  callback?: GenericCallback;
  metadata?: Metadata;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- top-level dialog config item; same heterogeneous payload rationale as DialogDataConfig
export interface DialogConfig<T = any> {
  width?: number;
  header: {
    title?: string;
    close?: boolean;
    titleClass?: string;
    titleIcon?: string;
    titleIconSize?: number;
  };
  content: {
    component?: Component;
    html?: string;
    height?: number;
    maxHeight?: number;
  };
  footer: {
    buttons: Array<DialogFooterBtn>;
  } | null;
  data: DialogDataConfig<T>;
  disable: boolean;
  closeCallback?: GenericCallback;
  disablePersistentAnimation?: boolean;
  scrollable?: boolean;
  fullscreen?: boolean;
  persistOnAction?: boolean;
  disclaimer: string | null;
  customErrorHandling?: boolean;
  showValidationAlert?: boolean;
}

interface DialogsState {
  dialogConfig: DialogConfig;
  showDialog: boolean;
}

export const defaultDialogsConfig: DialogConfig = {
  header: {
    title: "",
    close: true,
  },
  content: {},
  footer: {
    buttons: [
      {
        title: i18n.global.t("general.cancel"),
        spacer: "before",
        type: "text",
        cancel: true,
      },
    ],
  },
  data: {
    action: "",
    item: undefined,
  },
  disable: false,
  disclaimer: null,
};

const defaultDialogsState: DialogsState = {
  dialogConfig: { ...defaultDialogsConfig },
  showDialog: false,
};

export const useDialogsStore = defineStore(StoreModule.DIALOGS, {
  state: (): DialogsState => ({ ...defaultDialogsState }),
  persist: false,
  actions: {
    openDialog(config: DialogConfig) {
      this.dialogConfig = cloneDeep(config);
      this.showDialog = true;
    },
    closeDialog() {
      this.showDialog = false;
    },
  },
});
